import * as React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import SelectBox from "../../../components/atoms/select";
import {
  useRoundScoreOrderList,
  useScoreCalendarList,
  useScoreOrderList,
} from "../../../@apis/hooks/useTournament";
import { useGetRoundList } from "../../../@apis/hooks/useScore";
import type { TournamentType } from "../../../@apis/dto/response/main.response";
import { cn } from "../../../lib/utils/cn";
import {
  formatPeoria,
  hasRankingScore,
  rankingDisplay,
} from "../../../lib/utils/tournamentUtils";
import ParticipantStatusBadge from "../../../components/atoms/ParticipantStatusBadge";
import RankingNotice from "../../../components/atoms/RankingNotice";
import {
  ScoreCardViewPanel,
  UserScoreCardsPanel,
} from "../../../components/layout/score/TournamentScoreCardPanels";

interface RankingTabProps {
  tournamentId?: number | null;
  tournamentType?: TournamentType | null;
  defaultCalendarId?: number | null; // 일정대회 셀렉트 기본값(본인 스코어카드 일정)
}

// MULTI_ROUND 전용 전체 선택 옵션 값
const ALL_VALUE = "all";

// 정렬 우선순위: 참가 → 컷오프 → 실격 → 기권 → 불참
const STATUS_PRIORITY: Record<string, number> = {
  PARTICIPATED: 0,
  CUTOFF: 1,
  DISQUALIFIED: 2,
  WITHDRAWN: 3,
  ABSENT: 4,
};

interface RankingRow {
  id: string;
  status: string;
  rank: string; // "2T" 형태
  name: string;
  combinedScore: number | null; // 합산 스코어 (오버/언더)
  currentRoundScore: number | null; // 금회 스코어 (오버/언더)
  progressDisplay: string;
  order: number; // 원본 순서(순위순 유지용)
  isMe: boolean;
  scoreCardId: number | null; // 스코어 카드 단건조회용
  calendarId: number | null; // 스코어 카드 단건/묶음 조회용
  userId: number | null; // 멀티라운드 스코어카드 묶음 조회용
  excludeRoundId: number | null; // 비참가(실격/기권/불참) 처리된 라운드 → 카드 제외용
  scoreSum?: number;
  strokeHandicap?: number | null;
  totalScore?: number;
  peoriaScore?: number;
  peoriaHandicap?: number;
}

// 합산 타수에서 타수 핸디를 뺀 값
const netScoreSum = (
  scoreSum: number | null | undefined,
  strokeHandicap: number | null | undefined,
) => (scoreSum == null ? null : scoreSum - (strokeHandicap ?? 0));

// 합산 타수 옆 오버/언더에서 타수 핸디를 뺀 값
const adjustedCombinedScore = (
  combinedScore: number | null,
  strokeHandicap: number | null | undefined,
) =>
  combinedScore == null ? null : combinedScore - (strokeHandicap ?? 0);

// 최종 점수에서 타수 핸디를 뺀 값
const adjustedPeoria = (
  value: number | null | undefined,
  strokeHandicap: number | null | undefined,
) => (value == null ? undefined : value - (strokeHandicap ?? 0));

// 정수면 소수점 없이, 아니면 소수 한 자리
const formatHandicapValue = (value: number) => {
  const text = value.toFixed(1);
  return text.endsWith(".0") ? text.slice(0, -2) : text;
};

// 타수 핸디는 총점에서 빼는 값이므로 음수로 보여 준다
const finalHandicapLabel = (
  strokeHandicap: number | null | undefined,
  peoriaHandicap: number | null | undefined,
) => {
  const parts: string[] = [];
  if (peoriaHandicap != null)
    parts.push(`신페리오 핸디 ${formatHandicapValue(peoriaHandicap)}`);
  if (strokeHandicap != null)
    parts.push(`핸디 ${formatHandicapValue(-strokeHandicap)}`);
  return parts.length > 0 ? ` (${parts.join(" ")})` : "";
};

// 오버/언더 스코어 표기: 0 → E, 양수 → +n, 음수 → n
const formatScore = (v: number | null, scoreSum: number | null) =>
  v == null
    ? "-"
    : v === 0
      ? `${scoreSum}(E)`
      : v > 0
        ? `${scoreSum}(+${v})`
        : `${scoreSum}(${String(v)})`;

export default function RankingTab({
  tournamentId,
  tournamentType,
  defaultCalendarId,
}: RankingTabProps) {
  const isLegacy = tournamentType === "LEGACY";
  const isMultiRound = tournamentType === "MULTI_ROUND";

  // LEGACY: 스코어 일정 목록 / MULTI_ROUND: 대회 라운드 목록
  const { data: scoreCalendarData } = useQuery({
    ...useScoreCalendarList(isLegacy ? (tournamentId ?? 0) : 0),
  });
  const { data: roundListData } = useQuery({
    ...useGetRoundList(isMultiRound ? tournamentId : null),
  });

  // 비참가 라운드 태그명 조회용 (id → roundName)
  const roundNameById = React.useMemo(() => {
    const map = new Map<number, string>();
    (roundListData?.data?.roundList ?? []).forEach((r) =>
      map.set(r.id, r.roundName),
    );
    return map;
  }, [roundListData]);

  // 누적 노출 판정용 (id → roundOrder)
  const roundOrderById = React.useMemo(() => {
    const map = new Map<number, number>();
    (roundListData?.data?.roundList ?? []).forEach((r) =>
      map.set(r.id, r.roundOrder),
    );
    return map;
  }, [roundListData]);

  const options = React.useMemo(() => {
    if (isLegacy) {
      return (scoreCalendarData?.data?.calendarList ?? []).map((item) => ({
        label: `${item.date} ${item.timeType}`,
        value: String(item.id),
      }));
    }
    if (isMultiRound) {
      const roundOptions = (roundListData?.data?.roundList ?? []).map(
        (item) => ({
          label: item.roundName,
          value: String(item.id),
        }),
      );
      // MULTI_ROUND일 때만 전체 선택 가능
      return [{ label: "전체", value: ALL_VALUE }, ...roundOptions];
    }
    return [];
  }, [isLegacy, isMultiRound, scoreCalendarData, roundListData]);

  const [selected, setSelected] = React.useState("");
  const [expandedId, setExpandedId] = React.useState<string | null>(null);
  // 성별 필터 (멀티라운드 전용): 전체="", 여자="0", 남자="1"
  const [gender, setGender] = React.useState("");

  // 옵션이 로드되면 기본 선택 (일정대회는 본인 스코어카드 일정 우선)
  React.useEffect(() => {
    if (options.length === 0) return;
    if (options.some((o) => o.value === selected)) return;
    const preferred =
      isLegacy && defaultCalendarId != null
        ? options.find((o) => o.value === String(defaultCalendarId))?.value
        : undefined;
    setSelected(preferred ?? options[0].value);
  }, [options, selected, isLegacy, defaultCalendarId]);

  // LEGACY: 선택한 일정(calendarId) 기준 순위 조회
  const { data: scoreOrderData } = useQuery({
    ...useScoreOrderList(
      isLegacy ? (tournamentId ?? 0) : 0,
      isLegacy ? selected : null,
    ),
  });

  // 선택 전·전체는 roundId 없이 조회
  const roundIdParam =
    isMultiRound && selected && selected !== ALL_VALUE ? selected : null;
  // MULTI_ROUND: 선택한 라운드(roundId)/성별 기준 순위 조회
  const { data: roundScoreOrderData } = useQuery({
    ...useRoundScoreOrderList(
      isMultiRound ? (tournamentId ?? 0) : 0,
      roundIdParam,
      gender === "" ? null : (Number(gender) as 0 | 1),
    ),
  });

  const orderData = isLegacy
    ? scoreOrderData?.data
    : isMultiRound
      ? roundScoreOrderData?.data
      : undefined;
  const { showPeoria, notice } = rankingDisplay(
    orderData,
    isMultiRound && roundIdParam != null,
  );

  const rows: RankingRow[] = React.useMemo(() => {
    const scoreList = orderData?.scoreList ?? [];
    // 참가/컷오프(스코어 보유)
    const scoreRows: RankingRow[] = scoreList.map((item, i) => ({
      id: `s-${item.id}`,
      status: item.status,
      rank: item.rank,
      name: item.userName,
      combinedScore: item.combinedScore,
      currentRoundScore: item.currentRoundScore,
      progressDisplay: item.progressDisplay,
      order: i,
      isMe: item.isMe,
      scoreCardId: item.id,
      calendarId: item.calendarId,
      userId: item.userId,
      excludeRoundId: null,
      scoreSum: item.scoreSum,
      strokeHandicap: item.strokeHandicap,
      totalScore: item.totalScore,
      peoriaScore: item.peoriaScore,
      peoriaHandicap: item.peoriaHandicap,
    }));

    // 비참가자에는 calendarId가 없어 참가자 항목에서 가져옴 (멀티라운드 묶음 조회용)
    const sharedCalendarId = scoreList[0]?.calendarId ?? null;

    // 실격/기권/불참(비참가)
    // 멀티라운드: 다른 라운드에서 친 스코어카드가 있을 수 있어 펼침 허용(userId 보유)
    const nonParticipants = orderData?.nonParticipants ?? [];
    const nonRows: RankingRow[] = nonParticipants.map((item) => ({
      id: `n-${item.applyId}`,
      status: item.status,
      rank: "-",
      name: item.userName,
      combinedScore: null,
      currentRoundScore: null,
      progressDisplay: "-",
      order: 0,
      isMe: item.isMe,
      scoreCardId: null,
      calendarId: isMultiRound ? sharedCalendarId : null,
      userId: isMultiRound ? item.userId : null,
      // 비참가 처리된 라운드 → 전체 보기에서도 해당 라운드 카드 제외
      excludeRoundId: isMultiRound ? item.roundId : null,
    }));

    // 순서: 참가 → 컷오프(순위순) → 실격(가나다) → 기권(가나다) → 불참(가나다)
    return [...scoreRows, ...nonRows].sort((a, b) => {
      const pa = STATUS_PRIORITY[a.status] ?? 99;
      const pb = STATUS_PRIORITY[b.status] ?? 99;
      if (pa !== pb) return pa - pb;
      // 참가/컷오프는 원본 순위순 유지, 그 외는 이름 가나다순
      if (a.status === "PARTICIPATED" || a.status === "CUTOFF") {
        return a.order - b.order;
      }
      return a.name.localeCompare(b.name, "ko");
    });
  }, [isMultiRound, orderData]);

  // 라운드 대회는 합산 스코어 옆에 금회 스코어 컬럼 추가
  // 헤더와 각 행이 별도 grid라 auto 대신 고정폭이어야 열이 행끼리 맞는다
  // em 기준이라 글자 크기와 함께 늘고, 화면보다 넓어지면 가로 스크롤한다
  const gridCols = isMultiRound
    ? "grid-cols-[max(3.75rem,4em)_minmax(6rem,1fr)_max(4.375rem,5.5em)_max(4.375rem,5.5em)_max(3.125rem,4.5em)]"
    : "grid-cols-[max(3.75rem,4em)_minmax(6rem,1fr)_max(6.25rem,6em)_max(5rem,5em)]";

  // 나의 순위 (참가자 목록 중 본인 항목)
  const myRow = React.useMemo(() => rows.find((r) => r.isMe), [rows]);

  // MVP 안내: 라운드 '전체' + 성별(남/여) 선택 시에만 전체 MVP 제외 기준
  const showMvpNotice = isMultiRound && (orderData?.isMvpSelect ?? false);
  const isMvpExcluded = selected === ALL_VALUE && gender !== "";

  return (
    <div className="py-[15px]">
      <SelectBox
        options={options}
        value={selected}
        onChange={(e) => {
          setSelected(e.target.value);
          setExpandedId(null);
        }}
        placeholder="선택"
        className="h-auto py-[12px] pl-[16px] pr-[40px] text-base"
      />
      {/* 나의 순위 박스 */}
      {myRow && (
        <div className="mt-[15px] rounded-[10px] bg-amber-100 px-[15px] py-[14px]">
          <div className="flex items-center justify-between">
            <span className="text-sm font-bold">나의 순위</span>
            <span className="text-lg font-bold text-[#222]">
              {myRow.status === "PARTICIPATED" ? (
                myRow.rank
              ) : (
                <ParticipantStatusBadge status={myRow.status} />
              )}
            </span>
          </div>
          <div className="mt-[10px] flex flex-wrap items-baseline justify-between gap-x-[12px] gap-y-[6px]">
            <span className="min-w-0 break-keep text-sm font-[500] text-[#222]">
              {myRow.name}
            </span>
            <div className="ml-auto flex flex-wrap items-center justify-end gap-x-[12px] gap-y-[4px] text-sm">
              <span className="font-[500] leading-tight text-blue-600 whitespace-nowrap">
                합산{" "}
                {formatScore(
                  adjustedCombinedScore(
                    myRow.combinedScore,
                    myRow.strokeHandicap,
                  ),
                  netScoreSum(myRow.scoreSum, myRow.strokeHandicap),
                )}
              </span>
              {isMultiRound && (
                <span className="font-[500] leading-tight text-[#222] whitespace-nowrap">
                  금회 {formatScore(myRow.currentRoundScore, myRow.totalScore!)}
                </span>
              )}
              <span className="font-bold leading-tight text-[#222] whitespace-nowrap">
                {myRow.progressDisplay}
              </span>
            </div>
          </div>
          {showPeoria && hasRankingScore(myRow.status) && (
            <p className="mt-[6px] text-right text-sm font-[500] text-[#222]">
              최종{" "}
              {formatPeoria(
                adjustedPeoria(myRow.peoriaScore, myRow.strokeHandicap),
              )}
              {finalHandicapLabel(myRow.strokeHandicap, myRow.peoriaHandicap)}
            </p>
          )}
        </div>
      )}
      {/* 성별 필터 (라운드 대회 전용, 단일 선택) */}
      {isMultiRound && (
        <div className="mt-[15px] flex flex-wrap items-center gap-x-[16px] gap-y-[8px]">
          {[
            { label: "전체", value: "" },
            { label: "남자", value: "1" },
            { label: "여자", value: "0" },
          ].map((opt) => (
            <label
              key={opt.value}
              className="flex items-center gap-[8px] cursor-pointer select-none"
            >
              <input
                type="radio"
                name="ranking-gender"
                className="size-3 accent-primary cursor-pointer"
                value={opt.value}
                checked={gender === opt.value}
                onChange={() => {
                  setGender(opt.value);
                  setExpandedId(null);
                }}
              />
              <span className="text-sm text-[#222]">{opt.label}</span>
            </label>
          ))}
        </div>
      )}
      {showMvpNotice && (
        <p className="mt-[15px] break-keep text-[0.9375rem] font-bold text-red-500">
          {isMvpExcluded ? (
            <>
              전체 MVP{" "}
              <span className="rounded-[4px] bg-red-500 px-[6px] py-[2px] text-white">
                미포함
              </span>
            </>
          ) : (
            "전체 MVP 포함"
          )}
        </p>
      )}
      {rows.length > 0 && (
        <RankingNotice lines={notice} className="mt-[15px]" />
      )}
      {/* 순위 테이블 */}
      <div className="@container mt-[15px] overflow-x-auto overflow-y-hidden rounded-[10px] border border-[#eee]">
        <div className="min-w-full w-max">
          {/* 헤더 */}
          <div
            className={cn(
              "grid items-center bg-[#f5f5f5] text-[#888] font-bold text-sm",
              gridCols,
            )}
          >
            <div className="whitespace-nowrap px-[8px] py-[10px] text-center">
              순위
            </div>
            <div className="px-[10px] py-[10px]">이름</div>
            <div className="whitespace-nowrap px-[6px] py-[10px] text-left">
              합산 타수
            </div>
            {isMultiRound && (
              <div className="whitespace-nowrap px-[6px] py-[10px] text-left">
                금회 타수
              </div>
            )}
            <div className="whitespace-nowrap px-[6px] py-[10px] text-left">
              진행 홀
            </div>
          </div>

          {/* 본문 */}
          {rows.length === 0 ? (
            <div className="flex items-center justify-center py-[60px] text-sm text-[#999]">
              순위 정보가 없습니다.
            </div>
          ) : (
            rows.map((row) => {
              const isCutoff = row.status === "CUTOFF";
              // 실격/기권/불참 (스코어 카드 없음)
              const isDisqualifiedGroup =
                row.status === "DISQUALIFIED" ||
                row.status === "WITHDRAWN" ||
                row.status === "ABSENT";
              const showScore = hasRankingScore(row.status);
              // 펼침 가능: 일정대회는 모든 행(비참가자는 빈 카드), 멀티라운드는 사용자 보유 행
              const canExpand = isLegacy
                ? true
                : isMultiRound
                  ? row.userId != null && row.calendarId != null
                  : false;
              const isExpanded = canExpand && expandedId === row.id;
              return (
                <div key={row.id} className="border-t border-[#eee]">
                  <div
                    onClick={() =>
                      canExpand &&
                      setExpandedId((prev) => (prev === row.id ? null : row.id))
                    }
                    className={cn(
                      "grid items-center text-[#222] text-sm",
                      gridCols,
                      { "bg-[#F2F2F7]": isCutoff },
                      { "bg-[#D1D1D6]": isDisqualifiedGroup },
                      { "bg-amber-100": row.isMe },
                      canExpand && "cursor-pointer",
                      canExpand && !isExpanded && "hover:bg-gray-50",
                      !canExpand && isDisqualifiedGroup && "cursor-not-allowed",
                      { "bg-[#EAF6FF]": isExpanded },
                    )}
                  >
                    <div className="whitespace-nowrap px-[8px] py-[12px] text-center font-bold">
                      {row.status === "PARTICIPATED" ? (
                        row.rank
                      ) : (
                        <ParticipantStatusBadge status={row.status} />
                      )}
                    </div>
                    <div className="flex min-w-0 items-center gap-[6px] px-[10px] py-[12px]">
                      <p className={cn("truncate", { "font-bold": row.isMe })}>
                        {row.name}
                        {row.isMe && <span> (나)</span>}
                      </p>
                    </div>
                    <div className="whitespace-nowrap px-[6px] py-[12px] text-left font-[500] text-blue-600">
                      {showScore
                        ? formatScore(
                          adjustedCombinedScore(
                            row.combinedScore,
                            row.strokeHandicap,
                          ),
                          netScoreSum(row.scoreSum, row.strokeHandicap),
                        )
                        : "-"}
                      {showPeoria && showScore && (
                        <p className="text-xs text-[#222] whitespace-nowrap">
                          최종{" "}
                          {formatPeoria(
                            adjustedPeoria(row.peoriaScore, row.strokeHandicap),
                          )}
                        </p>
                      )}
                    </div>
                    {isMultiRound && (
                      <div className="whitespace-nowrap px-[6px] py-[12px] text-left font-[500]">
                        {showScore
                          ? formatScore(row.currentRoundScore, row.totalScore!)
                          : "-"}
                      </div>
                    )}
                    <div
                      className={cn(
                        "whitespace-nowrap px-[6px] py-[12px] text-left font-bold",
                        row.progressDisplay.includes("H")
                          ? "text-gray-500"
                          : "text-black",
                      )}
                    >
                      {row.progressDisplay}
                    </div>
                  </div>

                  <AnimatePresence initial={false}>
                    {isExpanded && (
                      <motion.div
                        key="panel"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: "easeInOut" }}
                        className="sticky left-0 w-[100cqw] overflow-hidden"
                      >
                        {isMultiRound ? (
                          <UserScoreCardsPanel
                            tournamentId={tournamentId}
                            calendarId={row.calendarId}
                            userId={row.userId}
                            nonPartRoundId={row.excludeRoundId}
                            nonPartRoundName={
                              row.excludeRoundId != null
                                ? roundNameById.get(row.excludeRoundId)
                                : undefined
                            }
                            roundOrderById={roundOrderById}
                            maxRoundOrder={
                              // 선택 라운드까지 누적 노출 (전체면 제한 없음)
                              selected === ALL_VALUE
                                ? null
                                : (roundOrderById.get(Number(selected)) ?? null)
                            }
                          />
                        ) : (
                          <ScoreCardViewPanel
                            tournamentId={tournamentId}
                            calendarId={row.calendarId}
                            scoreCardId={row.scoreCardId}
                          />
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
